/** @file Weights panel: the mesh's bones (pick the paint bone) and the selected vertices' influences. */
import type { Id } from '@pose2d/format/types';
import { useState } from 'react';
import { brushStore } from '../../tools/paintWeights/brushStore';
import { Button } from '../../ui-kit/Button';
import { NumberField } from '../../ui-kit/NumberField';
import { Select } from '../../ui-kit/Select';
import type { SectionProps } from './BindingSection';
import { weightsCalls } from './weightsCalls';

/**
 * Bones with weight (click = paint with it), then one share field per influence of the selected vertices; a share
 * edit in Setup mode on a stale bind commits mesh.rebindMesh first in the same batch (weightsCalls.edit, T-473).
 */
export function InfluenceSection({ view, doc, t, commit, autoNormalize, addValue, animate }: SectionProps & { autoNormalize: boolean; addValue: number; animate: boolean }) {
  const [adding, setAdding] = useState<Id>(view.choices[0]?.id ?? '');
  const rigid = view.binding === 'rigid';
  const share = (bone: Id, weight: number): void => {
    const batch = weightsCalls.edit(doc, view.target, weightsCalls.share(view.target, view.selected, bone, weight, autoNormalize), animate);
    if (batch.length > 0) commit(batch);
  };
  return (
    <section className="weights-section" aria-label={t('panels.weights.bones')}>
      <ul className="weights-bones">
        {view.bones.map((b) => (
          <li key={b.id}>
            <Button label={b.name} primary={b.id === view.paintBone} title={t('panels.weights.paintWith', { bone: b.name })} onClick={() => { brushStore.setState({ bone: b.id }); }} />
            <span className="weights-muted">{t('panels.weights.vertexCount', { n: b.vertices })}</span>
          </li>
        ))}
      </ul>
      {view.selected.length === 0 ? <p className="weights-muted">{t('panels.weights.noVertices')}</p> : null}
      {view.selected.length === 0 ? null : view.influences.map((r) => (
        <div key={r.id} className="weights-row">
          <NumberField label={r.name} value={r.weight} min={0} max={1} step={0.01} precision={3} disabled={rigid} onCommit={(w) => { share(r.id, w); }} />
          <Button label={t('panels.weights.remove')} danger disabled={rigid} onClick={() => { share(r.id, 0); }} />
        </div>
      ))}
      {view.selected.length === 0 ? null : (
        <div className="weights-row">
          <Select label={t('panels.weights.addBone')} value={adding} options={view.choices.map((c) => ({ value: c.id, label: c.name }))} onChange={setAdding} />
          <Button label={t('panels.weights.add')} disabled={rigid || adding === ''} onClick={() => { share(adding, addValue); }} />
        </div>
      )}
    </section>
  );
}
